import { useState } from "react";

import { Button, Form, Input, InputNumber, Modal, message, Select } from "antd";

import closeIcon from "data/icons/close-gray.svg";
import api from "services/api.service";

import "./GenerateLinkModal.scss";

interface IProps {
  isGenerateLinkOpen: boolean;
  setIsGenerateLinkOpen: (value: boolean) => void;
  onSuccess?: () => void;
}

const GenerateLinkModal = ({ isGenerateLinkOpen, setIsGenerateLinkOpen, onSuccess }: IProps) => {
  const [ form ] = Form.useForm();
  const [ isLoading, setIsLoading ] = useState(false);

  const handleCompleteForm = async (values: any) => {
    setIsLoading(true);

    try {
      const sizeInBytes = values.size * (values.sizeUnit === "GB" ? 1024 * 1024 * 1024 : 1024 * 1024);

      const now = new Date();
      const expiryDate = new Date(now.getTime() + values.expiryDays * 24 * 60 * 60 * 1000);
      const expiryDateString = expiryDate.toISOString();

      await api.createSharedStorageLink(values.marker || "", expiryDateString, sizeInBytes);

      message.success("Link generated successfully!");
      setIsGenerateLinkOpen(false);
      form.resetFields();
      onSuccess?.();
    } catch (e) {
      message.error("Something went wrong while generating the link!");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setIsGenerateLinkOpen(false);
    form.resetFields();
  };

  return (
    <Modal
      open={ isGenerateLinkOpen }
      closeIcon={
        <img
          alt=""
          src={ closeIcon }
          onClick={ handleCancel }
        />
      }
      footer={ null }
      centered
      className="generate-link-modal"
      width={ 500 }
      onCancel={ handleCancel }
    >
      <div className="generate-link-content">
        <h4>Generate Shared Drive</h4>

        <Form
          className="generate-link-form"
          name="generate-link-form"
          layout="vertical"
          autoComplete="off"
          form={ form }
          onFinish={ handleCompleteForm }
          initialValues={ {
            expiryDays: 30,
            size: 2,
            sizeUnit: "GB",
          } }
        >
          <div className="form-fields">
            <Form.Item
              className="mg-btn-0"
              label="Expiry in"
              name="expiryDays"
              rules={ [
                { required: true, message: "Please input expiry days!" },
              ] }
            >
              <div className="expiry-input-wrapper">
                <InputNumber
                  placeholder="30"
                  min={ 1 }
                  defaultValue={ 30 }
                  style={ { width: "100%" } }
                />
                <span className="expiry-label">days</span>
              </div>
            </Form.Item>

            <Form.Item
              className="mg-btn-0"
              label="Size limit"
              name="size"
              rules={ [
                { required: true, message: "Please input size limit!" },
              ] }
            >
              <div className="size-input-wrapper">
                <InputNumber
                  placeholder="100"
                  min={ 1 }
                  defaultValue={ 2 }
                  style={ { width: "100%" } }
                />
                <Form.Item
                  name="sizeUnit"
                  noStyle
                >
                  <Select
                    style={ { width: 80 } }
                    defaultValue="GB"
                    options={ [
                      { value: "MB", label: "MB" },
                      { value: "GB", label: "GB" },
                    ] }
                  />
                </Form.Item>
              </div>
            </Form.Item>

            <Form.Item
              className="mg-btn-0"
              label="Link Info"
              name="marker"
            >
              <Input
                placeholder="Link Info (Optional)"
              />
            </Form.Item>
          </div>

          <div className="buttons-wrapper">
            <Button
              type="default"
              onClick={ handleCancel }
              style={ { marginRight: 8 } }
            >
              Back
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={ isLoading }
            >
              Create
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default GenerateLinkModal;
